import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { LogsContext } from './logs.context';

import {
  DateFilter,
  SelectFilter,
  Table,
  TableCell,
  TableRow,
  WithSpinner,
} from '../../../components';
import {
  buildTableFilterBody,
  formatLocaleDate,
  isFilterActive,
} from '../../../utils';

import { config } from '../../../api/config';
import { actions } from '../../../state';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const LOG_TYPES = {
  851: 'UNSPECIFIED',
  852: 'INTERNAL_ERROR',
  853: 'DEVICE_ERROR',
  854: 'ADYEN_ERROR',
  855: 'UNKNOWN_DEVICE_STATUS',
  856: 'FAILED_DATABASE_SAVE',
  857: 'FAILED_NOTIFICATION',
  858: 'FAILED_EMAIL',
  859: 'FAILED_SMS',
  860: 'CACHE',
  861: 'FAILED_DEVICE_ACTION',
  862: 'DEVICE_RESPONSE',
  863: 'WEBHOOK_PAYLOAD',
  864: 'FAILED_CRON_JOB',
  865: 'H2_CARD_SYNC',
  866: 'RECOVERED_SELF_SERVICE_RESULT',
  867: 'BI_SYNC',
  868: 'BI_SYNC_ERROR',
  869: 'SELF_SERVICE_OVERCHARGE',
  870: 'APP_CHECK_FAILED',
  999: 'DEBUG',
};

const toNumberHeader = (values = []) =>
  values
    .filter(value => value !== null && value !== undefined)
    .map(value => ({ name: String(value), value }));

const toStringHeader = (values = []) =>
  values
    .filter(value => value !== null && value !== undefined)
    .map(value => ({ name: String(value), value: String(value) }));

const mapLogType = type => LOG_TYPES[type] || String(type);

const LogsPage = ({
  dispatch,
  logs: { loading, total, logs, header = {} },
}) => {
  const {
    page,
    setPage,
    filterBody,
    setFilterBody,
    idFilter,
    setIdFilter,
    typeFilter,
    setTypeFilter,
    messageFilter,
    setMessageFilter,
    stackFilter,
    setStackFilter,
    detailsFilter,
    setDetailsFilter,
    apiUrlFilter,
    setApiUrlFilter,
    httpRequestFilter,
    setHttpRequestFilter,
    httpResponseFilter,
    setHttpResponseFilter,
    dateTimeLtFilter,
    setDateTimeLtFilter,
    dateTimeGtFilter,
    setDateTimeGtFilter,
    paymentIdFilter,
    setPaymentIdFilter,
    orderIdFilter,
    setOrderIdFilter,
    membershipOrderIdFilter,
    setMembershipOrderIdFilter,
    paymentCardIdFilter,
    setPaymentCardIdFilter,
    deviceIdFilter,
    setDeviceIdFilter,
    clientIdFilter,
    setClientIdFilter,
    sortOrder,
    setSortOrder,
    clearFilters,
  } = useContext(LogsContext);

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const clearLogs = useCallback(() => {
    dispatch(actions.monitoring.clearLogsAction());
  }, [dispatch]);

  const fetchLogs = useCallback(
    data => {
      dispatch(actions.monitoring.getLogsAction(data));
    },
    [dispatch]
  );

  useEffect(
    () => () => {
      clearLogs();
      clearFilters();
    },
    [clearLogs]
  );

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchLogs({ ...(filterBody || {}), offset });
  }, [fetchLogs, filterBody, page]);

  const handleSubmit = useCallback(
    submitOptions => {
      const generatedFilter = [];

      if (idFilter.length) {
        generatedFilter.push({ field: 'id', value: idFilter });
      }
      if (typeFilter.length) {
        generatedFilter.push({ field: 'type', value: typeFilter });
      }
      if (messageFilter.length) {
        generatedFilter.push({ field: 'message', value: messageFilter });
      }
      if (stackFilter.length) {
        generatedFilter.push({ field: 'stack', value: stackFilter });
      }
      if (detailsFilter.length) {
        generatedFilter.push({ field: 'details', value: detailsFilter });
      }
      if (apiUrlFilter.length) {
        generatedFilter.push({ field: 'api_url', value: apiUrlFilter });
      }
      if (httpRequestFilter.length) {
        generatedFilter.push({
          field: 'http_request',
          value: httpRequestFilter,
        });
      }
      if (httpResponseFilter.length) {
        generatedFilter.push({
          field: 'http_response',
          value: httpResponseFilter,
        });
      }
      if (dateTimeGtFilter) {
        generatedFilter.push({
          field: 'created_at',
          expression: 'gte',
          value: dateTimeGtFilter,
          type: 'timestamp',
        });
      }
      if (dateTimeLtFilter) {
        generatedFilter.push({
          field: 'created_at',
          expression: 'lte',
          value: dateTimeLtFilter,
          type: 'timestamp',
        });
      }
      if (paymentIdFilter.length) {
        generatedFilter.push({ field: 'payment_id', value: paymentIdFilter });
      }
      if (orderIdFilter.length) {
        generatedFilter.push({ field: 'order_id', value: orderIdFilter });
      }
      if (membershipOrderIdFilter.length) {
        generatedFilter.push({
          field: 'membership_order_id',
          value: membershipOrderIdFilter,
        });
      }
      if (paymentCardIdFilter.length) {
        generatedFilter.push({
          field: 'payment_card_id',
          value: paymentCardIdFilter,
        });
      }
      if (deviceIdFilter.length) {
        generatedFilter.push({ field: 'device_id', value: deviceIdFilter });
      }
      if (clientIdFilter.length) {
        generatedFilter.push({ field: 'client_id', value: clientIdFilter });
      }

      setFilterBody(
        buildTableFilterBody({
          submitOptions,
          generatedFilter,
          sortOrder,
        })
      );
      setPage(1);
    },
    [
      apiUrlFilter,
      clientIdFilter,
      dateTimeGtFilter,
      dateTimeLtFilter,
      detailsFilter,
      deviceIdFilter,
      httpRequestFilter,
      httpResponseFilter,
      idFilter,
      membershipOrderIdFilter,
      messageFilter,
      orderIdFilter,
      paymentCardIdFilter,
      paymentIdFilter,
      setFilterBody,
      setPage,
      sortOrder,
      stackFilter,
      typeFilter,
    ]
  );

  const tableHeader = useMemo(() => {
    if (!header) {
      return [];
    }

    return [
      <SelectFilter
        title="ID"
        name="id"
        valueType="number"
        array={toNumberHeader(header.id)}
        filterState={idFilter}
        setFilterState={setIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', idFilter)}
      />,
      <SelectFilter
        title="Tipas"
        name="type"
        valueType="number"
        array={(header.type || []).map(type => ({
          name: mapLogType(type),
          value: type,
        }))}
        filterState={typeFilter}
        setFilterState={setTypeFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', typeFilter)}
      />,
      <SelectFilter
        title="Pranešimas"
        name="message"
        valueType="string"
        array={toStringHeader(header.message)}
        filterState={messageFilter}
        setFilterState={setMessageFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', messageFilter)}
      />,
      <SelectFilter
        title="Atsakymas (stack)"
        name="stack"
        valueType="string"
        array={toStringHeader(header.stack)}
        filterState={stackFilter}
        setFilterState={setStackFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', stackFilter)}
      />,
      <SelectFilter
        title="Atsakymas (details)"
        name="details"
        valueType="string"
        array={toStringHeader(header.details)}
        filterState={detailsFilter}
        setFilterState={setDetailsFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', detailsFilter)}
      />,
      <SelectFilter
        title="API URL"
        name="api_url"
        valueType="string"
        array={toStringHeader(header.api_url)}
        filterState={apiUrlFilter}
        setFilterState={setApiUrlFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', apiUrlFilter)}
      />,
      <SelectFilter
        title="HTTP užklausa"
        name="http_request"
        valueType="string"
        array={toStringHeader(header.http_request)}
        filterState={httpRequestFilter}
        setFilterState={setHttpRequestFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', httpRequestFilter)}
      />,
      <SelectFilter
        title="HTTP atsakymas"
        name="http_response"
        valueType="string"
        array={toStringHeader(header.http_response)}
        filterState={httpResponseFilter}
        setFilterState={setHttpResponseFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', httpResponseFilter)}
      />,
      <DateFilter
        title="Data ir laikas"
        name="created_at"
        lt={dateTimeLtFilter}
        setLt={setDateTimeLtFilter}
        gt={dateTimeGtFilter}
        setGt={setDateTimeGtFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('date', dateTimeLtFilter, dateTimeGtFilter)}
        titleWidth={'19rem'}
      />,
      <SelectFilter
        title="Apmokėjimo ID"
        name="payment_id"
        valueType="number"
        array={toNumberHeader(header.payment_id)}
        filterState={paymentIdFilter}
        setFilterState={setPaymentIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', paymentIdFilter)}
      />,
      <SelectFilter
        title="Užsakymo ID"
        name="order_id"
        valueType="number"
        array={toNumberHeader(header.order_id)}
        filterState={orderIdFilter}
        setFilterState={setOrderIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', orderIdFilter)}
      />,
      <SelectFilter
        title="Plano užsakymo ID"
        name="membership_order_id"
        valueType="number"
        array={toNumberHeader(header.membership_order_id)}
        filterState={membershipOrderIdFilter}
        setFilterState={setMembershipOrderIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', membershipOrderIdFilter)}
      />,
      <SelectFilter
        title="Apmokėjimo kortelės ID"
        name="payment_card_id"
        valueType="number"
        array={toNumberHeader(header.payment_card_id)}
        filterState={paymentCardIdFilter}
        setFilterState={setPaymentCardIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', paymentCardIdFilter)}
      />,
      <SelectFilter
        title="Įrenginio ID"
        name="device_id"
        valueType="number"
        array={toNumberHeader(header.device_id)}
        filterState={deviceIdFilter}
        setFilterState={setDeviceIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', deviceIdFilter)}
      />,
      <SelectFilter
        title="Kliento ID"
        name="client_id"
        valueType="number"
        array={toNumberHeader(header.client_id)}
        filterState={clientIdFilter}
        setFilterState={setClientIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', clientIdFilter)}
      />,
    ];
  }, [
    apiUrlFilter,
    clientIdFilter,
    dateTimeGtFilter,
    dateTimeLtFilter,
    detailsFilter,
    deviceIdFilter,
    handleSubmit,
    header,
    httpRequestFilter,
    httpResponseFilter,
    idFilter,
    membershipOrderIdFilter,
    messageFilter,
    orderIdFilter,
    paymentCardIdFilter,
    paymentIdFilter,
    setApiUrlFilter,
    setClientIdFilter,
    setDateTimeGtFilter,
    setDateTimeLtFilter,
    setDetailsFilter,
    setDeviceIdFilter,
    setHttpRequestFilter,
    setHttpResponseFilter,
    setIdFilter,
    setMembershipOrderIdFilter,
    setMessageFilter,
    setOrderIdFilter,
    setPaymentCardIdFilter,
    setPaymentIdFilter,
    setSortOrder,
    setStackFilter,
    setTypeFilter,
    sortOrder,
    stackFilter,
    typeFilter,
  ]);

  const renderTable = () =>
    logs.map(
      ({
        id,
        type,
        message,
        stack,
        details,
        api_url,
        http_request,
        http_response,
        created_at,
        payment_id,
        order_id,
        membership_order_id,
        payment_card_id,
        device_id,
        client_id,
      }) => (
        <TableRow key={id}>
          <TableCell align="center">{id}</TableCell>
          <TableCell>{mapLogType(type)}</TableCell>
          <TableCell>{message || '-'}</TableCell>
          <TableCell>{stack || '-'}</TableCell>
          <TableCell>{details || '-'}</TableCell>
          <TableCell>{api_url || '-'}</TableCell>
          <TableCell>{http_request || '-'}</TableCell>
          <TableCell>{http_response || '-'}</TableCell>
          <TableCell>
            {created_at ? formatLocaleDate(created_at) : '-'}
          </TableCell>
          <TableCell align="center">{payment_id ?? '-'}</TableCell>
          <TableCell align="center">{order_id ?? '-'}</TableCell>
          <TableCell align="center">{membership_order_id ?? '-'}</TableCell>
          <TableCell align="center">{payment_card_id ?? '-'}</TableCell>
          <TableCell align="center">{device_id ?? '-'}</TableCell>
          <TableCell align="center">{client_id ?? '-'}</TableCell>
        </TableRow>
      )
    );

  return (
    <WithSpinner loading={loading}>
      <Table
        header={tableHeader}
        isEmpty={!logs.length}
        filter={true}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  logs: state.monitoring.logs,
});

LogsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  logs: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    logs: PropTypes.array.isRequired,
    header: PropTypes.object,
  }).isRequired,
};

export default connect(mapStateToProps)(LogsPage);
